/**
 * =============================================================================
 *  PHAU THUAT CONTENT STREAM: DI CHUYEN / CO GIAN / XOA MOT ANH DA NHUNG
 * =============================================================================
 *
 *  Day la cho DUY NHAT trong ung dung ghi de len noi dung san co cua mot trang.
 *  Moi duong sua khac (chu, chu thich, thao tac trang) deu chi THEM vao. Nen tep
 *  nay duoc viet theo mot ky luat chat hon phan con lai:
 *
 *    1. Moi phep sua deu la CAT VA NOI tren mang byte da giai nen. Khong dung
 *       lai content stream, khong sinh lai toan tu nao ta khong tu tay doi.
 *    2. Cac phep cat duoc ap TU CUOI VE DAU. Ap tu dau ve cuoi thi phep cat thu
 *       hai tro toi cac vi tri da xe dich - va no van chay, van cho ra mot tep
 *       mo duoc, chi la cat vao giua mot toan tu khac.
 *    3. Mot ly do tu choi o BAT KY khau nao huy CA lan sua, khong sua mot phan.
 *       Mot trang sua nua chung la mot trang hong.
 *
 * =============================================================================
 *  VI SAO LENH MANG SAN MA TRAN MOI CHU KHONG MANG HOP CHU NHAT
 * =============================================================================
 *  Ma tran `cm` trong luong la ma tran CUC BO; thu nguoi dung keo tren man hinh
 *  la hop chu nhat trong he toa do TRANG. Doi tu cai nay sang cai kia can biet
 *  phan bien doi BAO NGOAI, va phan do chi biet duoc luc pdf.js dung danh sach
 *  toan tu (xem embeddedImages.ts).
 *
 *  Neu lenh mang hop chu nhat, duong ghi se phai lam lai phep doi do bang mot
 *  nhanh ma thu hai - va hai nhanh se troi khoi nhau, dung nhu bai hoc so 3 cua
 *  du an. Nen lenh mang thang MA TRAN DA TINH XONG: mot phep tinh, o tang biet
 *  du du lieu de tinh, va duong ghi chi con viec thay sau con so.
 */
import { PDFDocument } from 'pdf-lib';
import type { PdfMatrix } from '@pdfeditor/types';

import { formatMatrix } from './matrix';
import { findDrawByOccurrence, refusalFor, scanImageDraws, type ImageScan } from './imageBlocks';
import { imageXObjectNames, readPageContent } from './pageContent';

/** Mot thao tac tren mot lan ve anh cu the cua mot trang. */
export type ImageContentEdit =
  /** Ghi lai sau con so cua `cm`. Di chuyen va co gian la cung mot thao tac. */
  | { kind: 'transform'; occurrence: number; matrix: PdfMatrix }
  /** Cat bo ca khoi q...Q. */
  | { kind: 'remove'; occurrence: number };

export type ImageEditResult =
  | { ok: true; bytes: Uint8Array }
  | { ok: false; reason: string };

/**
 * Ap toan bo thao tac anh CUA MOT TRANG len byte PDF, tra ve byte moi.
 *
 * @param pdfBytes byte dau vao. KHONG BAO GIO bi ghi vao (ADR-003).
 */
export async function applyImageContentEdits(
  pdfBytes: Uint8Array,
  pageIndex: number,
  edits: readonly ImageContentEdit[],
): Promise<ImageEditResult> {
  if (edits.length === 0) return { ok: true, bytes: pdfBytes };

  let doc: PDFDocument;
  try {
    doc = await PDFDocument.load(pdfBytes, {
      ignoreEncryption: true,
      updateMetadata: false,
      throwOnInvalidObject: false,
    });
  } catch (err) {
    return {
      ok: false,
      reason: `Không mở được tài liệu để sửa ảnh (${String(err).slice(0, 120)}).`,
    };
  }

  const content = readPageContent(doc, pageIndex);
  if (!content.ok) return { ok: false, reason: content.reason };

  const scan = scanImageDraws(content.value.bytes);
  const imageNames = imageXObjectNames(doc, pageIndex);

  interface Splice {
    start: number;
    end: number;
    replacement: Uint8Array;
  }
  const splices: Splice[] = [];

  for (const edit of edits) {
    const block = findDrawByOccurrence(scan, edit.occurrence);
    if (!block) {
      const why = refusalFor(scan, edit.occurrence);
      return {
        ok: false,
        reason:
          why ??
          `Không tìm thấy chỗ vẽ ảnh thứ ${edit.occurrence + 1} trên trang ${pageIndex + 1} nữa. ` +
            'Tài liệu có thể đã đổi; hãy chọn lại ảnh.',
      };
    }

    // CUA CHAN: `Do` co the ve mot Form XObject (mot trang con) chu khong phai
    // anh. Cat nham mot Form la xoa mat ca mang noi dung.
    if (!imageNames.has(block.name)) {
      return {
        ok: false,
        reason:
          `Đối tượng "${block.name}" trên trang ${pageIndex + 1} không phải ảnh (/Subtype /Image) ` +
          'mà là một XObject dạng khác. Bản web chỉ sửa được ảnh.',
      };
    }

    if (edit.kind === 'transform') {
      if (!edit.matrix.every((v) => Number.isFinite(v))) {
        return { ok: false, reason: 'Ma trận đặt ảnh mới không phải số hợp lệ.' };
      }
      splices.push({
        start: block.cmStart,
        end: block.cmEnd,
        replacement: encode(`${formatMatrix(edit.matrix)} cm`),
      });
    } else {
      // Thay ca khoi bang mot ky tu xuong dong chu khong xoa han: giu cho hai
      // toan tu hai ben khong dinh vao nhau thanh mot tu vo nghia.
      splices.push({ start: block.blockStart, end: block.blockEnd, replacement: encode('\n') });
    }
  }

  // Hai thao tac cham vao cung mot vung byte = mot lenh se de len lenh kia mot
  // cach am tham. Bat o day chu khong de no thanh mot trang hong.
  const sorted = [...splices].sort((a, b) => a.start - b.start);
  for (let i = 1; i < sorted.length; i++) {
    const prev = sorted[i - 1] as Splice;
    const cur = sorted[i] as Splice;
    if (cur.start < prev.end) {
      return {
        ok: false,
        reason:
          'Hai thao tác ảnh cùng sửa vào một đoạn của luồng nội dung. Không áp dụng được cả hai ' +
          'mà không đoán thứ tự, nên thao tác bị từ chối.',
      };
    }
  }

  let out = content.value.bytes;
  // TU CUOI VE DAU - xem ky luat so 2 o dau tep.
  for (let i = sorted.length - 1; i >= 0; i--) {
    const s = sorted[i] as Splice;
    out = splice(out, s.start, s.end, s.replacement);
  }

  content.value.write(out);

  try {
    return { ok: true, bytes: await doc.save({ useObjectStreams: false }) };
  } catch (err) {
    return { ok: false, reason: `Không ghi được tệp sau khi sửa ảnh (${String(err).slice(0, 120)}).` };
  }
}

function encode(s: string): Uint8Array {
  const out = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) out[i] = s.charCodeAt(i) & 0xff;
  return out;
}

function splice(bytes: Uint8Array, start: number, end: number, replacement: Uint8Array): Uint8Array {
  const out = new Uint8Array(bytes.length - (end - start) + replacement.length);
  out.set(bytes.subarray(0, start), 0);
  out.set(replacement, start);
  out.set(bytes.subarray(end), start + replacement.length);
  return out;
}

/**
 * Quet mot trang cua mot tep PDF, chi de HOI: co bao nhieu cho ve anh, cai nao
 * sua duoc, cai nao khong va vi sao.
 *
 * Giao dien goi ham nay khi nguoi dung chon mot anh, de biet nen hien tay nam
 * hay hien mot loi tu choi doc duoc.
 */
export async function scanPageImages(
  pdfBytes: Uint8Array,
  pageIndex: number,
): Promise<{ ok: true; scan: ImageScan; imageNames: Set<string> } | { ok: false; reason: string }> {
  let doc: PDFDocument;
  try {
    doc = await PDFDocument.load(pdfBytes, {
      ignoreEncryption: true,
      updateMetadata: false,
      throwOnInvalidObject: false,
    });
  } catch (err) {
    return { ok: false, reason: `Không mở được tài liệu để đọc ảnh (${String(err).slice(0, 120)}).` };
  }
  const content = readPageContent(doc, pageIndex);
  if (!content.ok) return { ok: false, reason: content.reason };
  return {
    ok: true,
    scan: scanImageDraws(content.value.bytes),
    imageNames: imageXObjectNames(doc, pageIndex),
  };
}
